// Analytika vlastního vyhledávání (issue #68, epic #62).
//
// Jediná věc, kterou Luigi's Box uměl a my po jeho odpojení nemáme: **co lidi
// píší do hledání a na co pak kliknou**. Widget je náš, tak si to logujeme
// od prvního dne — bez toho nejde postavit týdenní smyčku synonym a nikdo
// nepozná, jestli vlastní index na produkci vůbec běží (pole `engine`).
//
// DVA KANÁLY, JEDNA FASÁDA (`trackSearch` / `trackClick` / `trackBuy` /
// `trackViewItemList` jsou jediné vstupní body):
//   1. **interní** — dávkový `sendBeacon` na náš API (`/v1/{tenant}/events`,
//      BQ `shared.search_events`); vstup smyčky synonym a zdravotní signál indexu.
//   2. **GA4** — přes nativní `gtag()` Shoptetu (`ga4.js`), event `site_search`
//      s parametry ve tvaru Luigi's Boxu (`service` / `interaction` /
//      `user_input` / `result_type` / `result_id`), aby šlo období LB
//      (`luigisbox`) a nového enginu porovnat v jednom reportu; k tomu
//      standardní `view_item_list` / `select_item` a session atribuce
//      (`search_session`, `search_last_term`) pro nativní `add_to_cart` /
//      `begin_checkout` / `purchase`. Definice metrik: docs/ops/search-ga4-mereni.md.
//
// ⚠️ ZÁSADA: **analytika nikdy nesmí shodit render ani rozbít odkaz.**
//    Celý modul je v try/catch a každá veřejná funkce je no-op, když cokoli
//    chybí. Když se rozbije měření, uživatel to nepozná.
//
// ⚠️ GUARD `getConfig()` JE V KAŽDÉ VEŘEJNÉ FUNKCI: když je vrstva vypnutá
//    (LB na stránce, B2B ceník, file://, breaker), neběží ani GA4 větev —
//    jinak by vedle LB chodily duplicitní eventy.
//
// PROKLIK = AKTIVACE ODKAZU, tedy `click` (+ `auxclick` prostředního tlačítka),
// NIKDY `pointerdown`. Do 2026-09-18 se měřilo z `pointerdown` („střílí před
// navigací, nerozbíjí Ctrl+klik") — jenže na dotyku `pointerdown` vystřelí už
// ve chvíli, kdy prst při SCROLLU dosedne na kartu. Každý posun výpisu byl
// „proklik" karty pod prstem: mobil 5,4 kliku na hledání, report CTR 279 %,
// nejhorší session 229 kliků na 140 kódů (handover reporting-saas #2438).
// `click` po scrollu ani swipu nevznikne (prohlížeč gesto zruší přes
// `pointercancel`), handler běží PŘED navigací a odkaz nechává být — žádné
// `preventDefault` + ruční `location.href`, takže Ctrl/Cmd+klik, Shift+klik
// i nová karta fungují dál. Beacon navigaci přežije sám (`sendBeacon`), gtag
// frontu při odchodu dopošle gtag.js. `click` navíc chytí Enter na fokusované
// kartě, který `pointerdown` míjel.
// ⚠️ Klikové metriky 11. 9. (cutover) → nasazení téhle opravy jsou na mobilu
// a tabletu nadhodnocené v OBOU kanálech (`shared.search_events` i GA4) — viz
// docs/ops/search-ga4-mereni.md §Známé díry.
//
// SOUHLASY (#68 §Souhlasy):
//   - **interní kanál**: anonymní události vlastní služby, `session_id` jen
//     v `sessionStorage` (umře se zavřením panelu), žádná cookie, žádný
//     cross-site identifikátor. Funkční měření vlastní funkce webu, bez souhlasu.
//   - **GA4 kanál**: souhlas řeší Google Consent Mode nad nativním gtag
//     (default denied → cookieless ping, po souhlasu plný hit) — stejně jako
//     nativní `search`/`view_item` Shoptetu i jako LB. V JS se negatuje.
//   - **`marketing`**: perzistentní identifikátor a personalizace — až slot 5
//     v S5, tenhle modul ho zatím nikde nezakládá.

import { sendEvent } from './searchApi.js';
import { getConfig } from './searchConfig.js';
import { ga4Event, ga4Set } from './ga4.js';
import L from '../locale/cs/searchAnalytics.js';

const SESSION_KEY = 'bs-search-sid';
// Značka QA provozu: session id z automatizovaného prohlížeče začíná `qa-`
// a server takový řádek zahodí (`search/api/events.mjs isQaSession`). Bez ní
// dělal kanárek na ostré ~25 % všech session v `shared.search_events` —
// Playwright `route()` beacon z `pagehide` nezachytí (sonda
// `ops/sweeps/2026-09-16-qa-znacka-search-events/`). Harnessy si značku píšou
// i samy (`tools/lib/qa-session.mjs`), tohle je záchytná síť pro ty, co ne.
const QA_PREFIX = 'qa-';

// Dávkování: události se sbírají a odesílají jedním beaconem. Server bere
// max. 50 událostí v dávce (`search/api/app.mjs`), držíme se pod tím.
const BATCH_MAX = 25;
const BATCH_MS = 1000;

// GA4 — tvar Luigi's Boxu (viz hlavička). `SERVICE_*` jsou doslova hodnoty LB.
const GA4_EVENT = 'site_search';
export const SERVICE_SEARCH = 'Search';
export const SERVICE_AUTOCOMPLETE = 'AutoComplete';
export const LIST_SEARCH = 'search_results';
export const LIST_AUTOCOMPLETE = 'autocomplete';
const LIST_NAME = L.listName;
// LB posílá `display` našeptávače až 2 s po posledním dotazu — jeden event na
// dotaz, na kterém se uživatel zastavil, ne na každý stisk klávesy.
const AC_DISPLAY_MS = 2000;
const MAX_QUERY = 100;

// Session atribuce pro nativní eventy: klíč čte i inline snippet v hlavičce
// HTML kódů (`shoptet-includes/common-header.html`) — název i tvar
// `{q, ts}` a TTL se musí shodovat.
const ATTR_KEY = 'bs-search-attr';
const ATTR_TTL_MS = 30 * 60 * 1000;

// `kind` skupin v markupu → `result_type` (LB hodnoty). Vlastní render sekcí
// na stránce výsledků (`searchApiModel`/`searchNative`): cats/brands/posts/pages;
// našeptávač (`whispererRender` i nativní `tagLists`): cat/brand/article/page/other.
// Čipy kategorií pod H1 (2026-10-14) mají vlastní typ `category_chip`, aby šel
// proklik z čipu oddělit od řádku sekce Kategorie dole (oba = proklik na kategorii).
const RESULT_TYPES = {
  cat: 'category',
  cats: 'category',
  cat_chip: 'category_chip',
  category: 'category',
  brand: 'brand',
  brands: 'brand',
  article: 'article',
  posts: 'article',
  page: 'page',
  pages: 'page',
  other: 'page',
};

// Košíkové tlačítko uvnitř karty/řádku: klik na něm NENÍ proklik, hlásí se
// jako `buy`. `a.add-to-cart-button` je odkaz na detail u variant/nedostupných
// — to proklik je.
const BUY_SELECTOR = 'button.add-to-cart-button, [data-bs-cart]';

let queue = [];
// Konfigurace, pod kterou fronta události přijala — `flush()` s ní odešle
// i po shození breakeru (5xx), kdy `getConfig()` už vrací `null`.
let queueConfig = null;
let timer = null;
let wired = false;
let acDisplay = null; // čekající `display` našeptávače {timer, params}

// --------------------------------------------------------------- kontext

/**
 * Anonymní id session. `sessionStorage`, ne cookie — bez souhlasu nesmí
 * vzniknout nic, co přežije zavření panelu nebo se dostane na jiný web.
 */
function sessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (automated() ? QA_PREFIX : '') + randomId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    // private mode — měříme dál, jen se události nespojí do session
    return null;
  }
}

/**
 * Běží stránka v automatizovaném prohlížeči? `navigator.webdriver` nastavuje
 * Playwright/Puppeteer/Selenium (ověřeno headless i headed, desktop i emulace
 * iPhonu); reálný prohlížeč má `false`. Falešně pozitivní případ stojí pár
 * zahozených událostí, falešně negativní zanáší data celého shopu.
 */
function automated() {
  try {
    return navigator.webdriver === true;
  } catch (e) {
    return false;
  }
}

function randomId() {
  try {
    const buf = new Uint8Array(8);
    window.crypto.getRandomValues(buf);
    return Array.prototype.map.call(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return String(Math.random()).slice(2, 18);
  }
}

/**
 * Hrubý bucket zařízení. Shoptet renderuje jiné HTML podle User-Agentu
 * (`body.mobile` vs `body.desktop`) — server ví líp než šířka okna, jestli
 * uživatel dostal mobilní stránku.
 */
function device() {
  try {
    const body = document.body;
    if (body && body.classList.contains('mobile')) return 'mobile';
    if (window.innerWidth < 768) return 'mobile';
    if (window.innerWidth < 1024) return 'tablet';
    return 'desktop';
  } catch (e) {
    return null;
  }
}

function cleanQuery(query) {
  const q = query === null || query === undefined ? '' : String(query).trim();
  return q.length > MAX_QUERY ? q.slice(0, MAX_QUERY) : q;
}

// --------------------------------------------------------------- fronta

function schedule() {
  if (timer) return;
  timer = setTimeout(flush, BATCH_MS);
}

/** Odešle, co je ve frontě. Volá se z timeru, při odchodu ze stránky a u prokliků. */
export function flush() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  const batch = queue.slice(0, BATCH_MAX);
  queue = queue.slice(BATCH_MAX);
  sendEvent({ events: batch }, queueConfig);
  if (queue.length) schedule();
}

/**
 * Zařadí událost. `now: true` odešle hned — u prokliku se nedá čekat na timer,
 * uživatel je za milisekundu na jiné stránce. `config` = konfigurace zahájeného
 * dotazu (jen `trackSearch` po fallbacku), jinak platí guard `getConfig()`.
 */
function track(type, data, now, config) {
  try {
    const cfg = config || getConfig();
    if (!cfg) return; // Ú0 — vrstva vypnutá, neměříme nic
    queueConfig = cfg;
    const event = { type: type, sid: sessionId(), ts: Date.now(), device: device() };
    try {
      event.path = window.location.pathname;
    } catch (e) {
      /* nic */
    }
    Object.keys(data || {}).forEach(function (key) {
      const value = data[key];
      if (value !== null && value !== undefined && value !== '') event[key] = value;
    });
    queue.push(event);
    wire();
    if (now || queue.length >= BATCH_MAX) flush();
    else schedule();
  } catch (e) {
    /* analytika nikdy nehází */
  }
}

/**
 * Odeslání rozpracované fronty při odchodu. `pagehide` chytí i iOS, kde
 * `beforeunload` nefiruje; `visibilitychange` chytí přepnutí panelu.
 */
function wire() {
  if (wired) return;
  wired = true;
  try {
    window.addEventListener('pagehide', function () {
      flushDisplay();
      flush();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        flushDisplay();
        flush();
      }
    });
  } catch (e) {
    /* nic */
  }
}

// ------------------------------------------------------------ GA4 display

/** `site_search` display + značka session pro atribuci nativních eventů. */
function emitDisplay(params) {
  ga4Event(GA4_EVENT, params);
  markSearchSession(params.user_input);
}

/** Pošle čekající `display` našeptávače hned (před proklikem / odchodem). */
function flushDisplay() {
  if (!acDisplay) return;
  clearTimeout(acDisplay.timer);
  const params = acDisplay.params;
  acDisplay = null;
  emitDisplay(params);
}

function displayAutocomplete(params) {
  if (acDisplay) clearTimeout(acDisplay.timer);
  acDisplay = {
    params: params,
    timer: setTimeout(function () {
      acDisplay = null;
      emitDisplay(params);
    }, AC_DISPLAY_MS),
  };
}

// ------------------------------------------------------------- atribuce

/**
 * Zapíše „v téhle session se hledalo" pro nativní eventy Shoptetu.
 * `ga4Set` (druhý `gtag('config', G, …)`, viz ga4.js — `set` na ostré
 * parametr nepřibalí, #326) je dosadí ke každému dalšímu eventu na TÉHLE
 * stránce (`add_to_cart` po AJAXu košíku); na dalších stránkách to ze
 * `sessionStorage` udělá inline snippet v hlavičce HTML kódů — ten běží
 * před Shoptetím `gtag('config')` a merguje se do něj, náš bundle ne.
 */
export function markSearchSession(query) {
  const q = cleanQuery(query);
  try {
    sessionStorage.setItem(ATTR_KEY, JSON.stringify({ q: q, ts: Date.now() }));
  } catch (e) {
    /* private mode — atribuce zůstane jen na téhle stránce */
  }
  ga4Set({ search_session: '1', search_last_term: q });
}

/** Čerstvá značka session, nebo `null` (okno je pevných 30 min od posledního hledání). */
export function readSearchSession() {
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved.ts !== 'number') return null;
    if (Date.now() - saved.ts > ATTR_TTL_MS) return null;
    return { q: cleanQuery(saved.q), ts: saved.ts };
  } catch (e) {
    return null;
  }
}

/**
 * Boot: dosadí `search_session` z uložené značky i na stránkách bez hledání.
 * Pojistka pro hlavičkový snippet (kdyby vypadl nebo se Shoptetí `config`
 * pushl dřív) a pokrytí `add_to_cart` z detailu, kam uživatel přišel
 * z výsledků. `ts` se NEobnovuje — posuvné okno by atribuci táhlo donekonečna.
 */
export function initSearchAttribution() {
  try {
    if (!getConfig()) return;
    const saved = readSearchSession();
    if (!saved) return;
    ga4Set({ search_session: '1', search_last_term: saved.q });
  } catch (e) {
    /* nic */
  }
}

// --------------------------------------------------------- veřejné události

/**
 * Dotaz na stránce výsledků nebo v našeptávači.
 *
 * @param {object} p
 * @param {string} p.query
 * @param {number} p.results   počet výsledků (0 = zero-result, vstup smyčky synonym)
 * @param {number} [p.took]    doba odpovědi v ms (u fallbacku doba čekání na API)
 * @param {'typesense'|'shoptet_fallback'} p.engine
 * @param {string} [p.reason]  důvod fallbacku (`timeout | http_<status> | net | json | invalid`)
 * @param {boolean} [p.suggest] dotaz vznikl v našeptávači
 * @param {object} [p.config]  konfigurace, pod kterou dotaz na API odešel (stránka
 *   výsledků po fallbacku). 5xx mezitím shodí breaker a `getConfig()` vrátí `null` —
 *   bez tohohle by se `reason=http_5xx` neodeslal nikdy, tedy právě výpadky backendu
 *   (Codex review PR #377). Jen pro dotaz zahájený pod platnou konfigurací; ostatní
 *   události (kliky, košík) mají guard `getConfig()` beze změny.
 */
export function trackSearch(p) {
  try {
    if (!p) return;
    const cfg = p.config || getConfig();
    if (!cfg) return;
    const suggest = !!p.suggest;
    track(
      suggest ? 'autocomplete' : 'search',
      {
        q: p.query,
        results: p.results,
        took: p.took,
        engine: p.engine,
        reason: p.reason,
      },
      false,
      cfg,
    );
    // Nulový výsledek dostává vlastní typ — `v_search_zero_results` na něm stojí
    // a filtr na jeden `event_type` je levnější než na `results_count = 0`.
    if (p.results === 0 && !suggest) {
      track('zero_result', { q: p.query, results: 0, took: p.took, engine: p.engine, reason: p.reason }, false, cfg);
    }

    // GA4: `search_term` vedle `user_input` plní vestavěnou dimenzi „Search
    // term" bez registrace. Nativní event `search` Shoptetu se NEduplikuje —
    // ten na `/vyhledavani/` letí sám z hlavičky stránky.
    const q = cleanQuery(p.query);
    const params = {
      service: suggest ? SERVICE_AUTOCOMPLETE : SERVICE_SEARCH,
      interaction: 'display',
      user_input: q,
      search_term: q,
      results_count: typeof p.results === 'number' ? p.results : undefined,
      engine: p.engine,
    };
    if (suggest) displayAutocomplete(params);
    else emitDisplay(params);
  } catch (e) {
    /* analytika nikdy nehází */
  }
}

/**
 * Proklik na výsledek. Volá se z `click`/`auxclick` těsně před navigací,
 * takže musí odejít hned.
 *
 * @param {object} p
 * @param {string} [p.code]        kód produktu (produktový proklik)
 * @param {number} [p.position]    pozice ve výpisu (1 = první)
 * @param {string} [p.query]
 * @param {string} [p.service]     `Search` | `AutoComplete` (GA4), default Search
 * @param {string} [p.resultType]  GA4 `result_type` (default `product`)
 * @param {string} [p.resultId]    GA4 `result_id` (default kód produktu)
 * @param {string} [p.listId]      GA4 `item_list_id` pro `select_item`
 * @param {string} [p.slot]        doporučovací slot (S5), jinak výsledky hledání
 * @param {string} [p.anchor]      produkt, ze kterého se doporučovalo
 */
export function trackClick(p) {
  try {
    if (!getConfig() || !p) return;
    const type = p.resultType || 'product';
    const q = cleanQuery(p.query);
    // Interní kanál zná jen produktové prokliky (`clicked_code`), zbytek je
    // jen v GA4 — na tom stojí `v_search_ctr`.
    if (p.code) {
      track(
        p.slot ? 'rec_click' : 'click',
        { code: p.code, pos: p.position, q: p.query, slot: p.slot, anchor: p.anchor },
        true,
      );
    }

    flushDisplay(); // display našeptávače musí předcházet kliku
    ga4Event(GA4_EVENT, {
      service: p.service || SERVICE_SEARCH,
      interaction: 'click',
      user_input: q,
      search_term: q,
      result_type: type,
      result_id: p.resultId || p.code,
      position: type === 'product' ? p.position : undefined,
    });
    if (type === 'product' && p.code) {
      const listId = p.slot || p.listId || LIST_SEARCH;
      ga4Event('select_item', {
        item_list_id: listId,
        item_list_name: LIST_NAME,
        search_term: q,
        items: [{ item_id: p.code, index: p.position, item_list_id: listId, item_list_name: LIST_NAME }],
      });
    }
  } catch (e) {
    /* nic */
  }
}

/**
 * Přidání do košíku z výsledků / našeptávače (LB `interaction: buy`).
 * Jen GA4 — interní API typ `add_to_cart` nezná a neznámý typ shazuje celou
 * dávku (`search/api/events.mjs` EVENT_TYPES); nativní `add_to_cart` GA4
 * střílí Shoptet sám po AJAXu košíku.
 *
 * @param {object} p
 * @param {string} p.code
 * @param {number} [p.position]
 * @param {string} [p.query]
 * @param {string} [p.service]
 */
export function trackBuy(p) {
  try {
    if (!getConfig() || !p || !p.code) return;
    const q = cleanQuery(p.query);
    flushDisplay();
    ga4Event(GA4_EVENT, {
      service: p.service || SERVICE_SEARCH,
      interaction: 'buy',
      user_input: q,
      search_term: q,
      result_type: 'product',
      result_id: p.code,
      position: p.position,
    });
  } catch (e) {
    /* nic */
  }
}

/** Změna řazení výsledků. */
export function trackSort(order, query) {
  track('sort', { q: query, slot: order });
}

/** Dosypání další stránky výsledků. */
export function trackLoadMore(p) {
  track('load_more', { q: p && p.query, results: p && p.results, engine: p && p.engine });
}

/** Imprese doporučovacího slotu (S5). */
export function trackRecImpression(p) {
  track('rec_impression', { slot: p && p.slot, anchor: p && p.anchor, results: p && p.results });
}

/**
 * Imprese mřížky výsledků + GA4 `view_item_list`.
 *
 * ⚠️ PROČ VLASTNÍ EVENT: nativní `view_item_list` Shoptetu běží nad kartami,
 * které jsou v DOM při načtení stránky. Naše karty vznikají AŽ POTOM (výměna
 * obsahu `#products` z indexu, dosypání přes loadMore) — Shoptet je nezachytí
 * a v měření by vznikla díra, kterou nikdo nečeká. Na `/vyhledavani/` nativ
 * žádný `view_item_list` neposílá (jen `search`), kolize nehrozí.
 *
 * `index` je GLOBÁLNÍ (offset + pořadí v dávce), aby seděl s `pos` prokliku.
 *
 * @param {object} p
 * @param {Array<{sku?: string, code?: string, name?: string, price?: number}>} p.items
 * @param {string} [p.listName]  interní slot (S5), default search_results
 * @param {string} [p.listId]    GA4 item_list_id, default search_results
 * @param {number} [p.offset]    kolik karet už bylo zobrazeno před touhle dávkou
 * @param {string} [p.query]
 * @param {string} [p.engine]
 */
export function trackViewItemList(p) {
  try {
    if (!getConfig() || !p) return;
    const items = p.items || [];
    const slot = p.listName || LIST_SEARCH;
    track('view_item_list', { q: p.query, results: items.length, engine: p.engine, slot: slot });
    if (!items.length) return;

    const listId = p.listId || slot;
    const offset = p.offset || 0;
    const q = cleanQuery(p.query);
    ga4Event('view_item_list', {
      item_list_id: listId,
      item_list_name: LIST_NAME,
      search_term: q,
      items: items.map(function (item, i) {
        return {
          item_id: item.sku || item.code,
          item_name: item.name,
          price: item.price,
          index: offset + i + 1,
          item_list_id: listId,
          item_list_name: LIST_NAME,
        };
      }),
    });
  } catch (e) {
    /* nic */
  }
}

// ------------------------------------------------------- delegace prokliků

function pathOf(href) {
  try {
    return new URL(href, window.location.href).pathname;
  } catch (e) {
    return href || '';
  }
}

/**
 * Co uživatel v kontejneru trefil. `null` = nic měřitelného (hlavička, text).
 * Produkt = `[data-bs-code]` (karta výpisu i řádek našeptávače), ostatní
 * jen přes odkaz: našeptané dotazy (`.bs-wh-sug__item`), CTA „Zobrazit
 * všech N" (`.bs-wh__cta`), skupiny našeptávače (`ul[data-bs-kind]`) a
 * sekce stránky výsledků (`.bs-sgroup--<kind>`).
 */
/**
 * Patří uzel kontejneru `host`, ne vnořenému kontejneru s vlastní delegací?
 * Řada „Podobné skladem" žije od 2026-10-14 UVNITŘ mřížky výsledků (searchPage.js)
 * a měří se sama (`rec_click` se slotem), mřížka ji přeskočí a nepočítá její
 * karty do pozic.
 */
function ownedBy(host, node) {
  for (let n = node; n && n !== host; n = n.parentElement) {
    if (n.dataset && n.dataset.bsAnalytics) return false;
  }
  return true;
}

/** Karty kontejneru v pořadí DOM, bez karet vnořených kontejnerů. */
function ownCards(host) {
  return Array.prototype.filter.call(host.querySelectorAll('[data-bs-code]'), function (c) {
    return ownedBy(host, c);
  });
}

function classify(host, target, ctx) {
  const card = target.closest('[data-bs-code]');
  if (card && host.contains(card)) {
    return {
      code: card.getAttribute('data-bs-code'),
      position: ownCards(host).indexOf(card) + 1,
      resultType: 'product',
    };
  }
  const link = target.closest('a[href]');
  if (!link || !host.contains(link)) return null;
  if (link.classList.contains('bs-wh-sug__item')) {
    return { resultType: 'query', resultId: link.dataset.bsQuery || (link.textContent || '').trim() };
  }
  if (link.classList.contains('bs-wh__cta')) {
    return { resultType: 'query', resultId: cleanQuery(ctx && ctx.query) };
  }
  const list = link.closest('[data-bs-kind]');
  if (list && host.contains(list)) {
    return { resultType: RESULT_TYPES[list.dataset.bsKind] || 'page', resultId: pathOf(link.href) };
  }
  const group = link.closest('.bs-sgroup');
  if (group && host.contains(group)) {
    const m = /(?:^|\s)bs-sgroup--([\w-]+)/.exec(group.className);
    const kind = m ? m[1] : '';
    if (kind === 'products') return null; // hlavička sekce, ne výsledek
    return { resultType: RESULT_TYPES[kind] || 'page', resultId: pathOf(link.href) };
  }
  return null;
}

/**
 * Proklik na uzel v kontejneru. Volá ho `wireClicks` a cesty, kde `click`
 * nevzniká: Enter v našeptávači naviguje přes `location.href` (fokus drží
 * input, ne řádek). Vrací true, když se něco poslalo.
 *
 * @param {Element} host
 * @param {Element} node
 * @param {{query?: string, service?: string, listId?: string}} ctx
 */
export function trackNodeClick(host, node, ctx) {
  try {
    if (!host || !node) return false;
    const hit = classify(host, node, ctx);
    if (!hit) return false;
    trackClick({
      code: hit.code,
      position: hit.position,
      resultType: hit.resultType,
      resultId: hit.resultId,
      query: ctx && ctx.query,
      service: ctx && ctx.service,
      listId: ctx && ctx.listId,
      slot: ctx && ctx.slot,
      anchor: ctx && ctx.anchor,
    });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Zavěsí sledování prokliků a košíku na kontejner. Delegace, ne listener na
 * kartu: loadMore dosypává karty za běhu a re-vázat posluchače po každé
 * dávce je zbytečná práce navíc.
 *
 * Kód produktu čte z `data-bs-code` (plní `productCardRender.cardNode` a
 * `whispererRender.buildProduct`), pozici z pořadí karty v kontejneru —
 * obojí přežije dosypání i přeřazení. `ctx` se čte až v okamžiku prokliku
 * (volající ho přepisuje při každém dotazu).
 *
 * Proklik = aktivace ODKAZU v kontejneru (viz hlavička modulu, proč ne
 * `pointerdown`): `click` nese levé tlačítko, klepnutí, Ctrl/Cmd/Shift+klik
 * i Enter na fokusovaném odkazu, `auxclick` s `button === 1` prostřední
 * tlačítko (nová karta; pravé tlačítko jen otevírá menu a nic neslibuje).
 * Po scrollu ani swipu přes kartu prohlížeč `click` nevydá. Klik mimo odkaz
 * (cena, dostupnost, prázdná plocha karty) nikam nevede → proklik to není.
 *
 * Košík (`button.add-to-cart-button` karty, `[data-bs-cart]` řádku) hlásí
 * `buy` — vlastní listener tlačítka běží první a přidá do košíku,
 * `preventDefault` propagaci nezastaví. Proklik z něj NEvzniká.
 *
 * @param {Element} host
 * @param {{query?: string, service?: string, listId?: string, slot?: string, anchor?: string}} ctx
 */
export function wireClicks(host, ctx) {
  try {
    if (!host || host.dataset.bsAnalytics) return;
    host.dataset.bsAnalytics = '1';
    const onLink = function (e) {
      try {
        const target = e.target;
        if (!target || !target.closest) return;
        if (target.closest(BUY_SELECTOR)) return; // košík = buy, ne proklik
        const link = target.closest('a[href]');
        if (!link || !host.contains(link) || !ownedBy(host, link)) return;
        trackNodeClick(host, link, ctx);
      } catch (err) {
        /* analytika nikdy nerozbije klik */
      }
    };
    host.addEventListener('click', onLink);
    host.addEventListener('auxclick', function (e) {
      if (e.button === 1) onLink(e);
    });
    host.addEventListener('click', function (e) {
      try {
        const target = e.target;
        if (!target || !target.closest) return;
        const btn = target.closest(BUY_SELECTOR);
        if (!btn || !host.contains(btn) || !ownedBy(host, btn)) return;
        const card = btn.closest('[data-bs-code]');
        if (!card) return;
        trackBuy({
          code: card.getAttribute('data-bs-code'),
          position: ownCards(host).indexOf(card) + 1,
          query: ctx && ctx.query,
          service: ctx && ctx.service,
        });
      } catch (err) {
        /* nic */
      }
    });
  } catch (e) {
    /* nic */
  }
}

/** Jen pro testy — vyprázdní frontu bez odeslání. */
export function resetAnalytics() {
  queue = [];
  if (timer) clearTimeout(timer);
  timer = null;
  if (acDisplay) clearTimeout(acDisplay.timer);
  acDisplay = null;
}
